import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { restApiUrl } from 'appConstants';
import { SpecialDateRequest } from 'appTypes';
import { axiosInstance } from 'axios_middleware';
import { ProductInfo } from 'features/app_header/productSwitcherConstants';
import { walkmeService } from 'features/login/WalkMe';
import { Point, Polygon } from 'geojson';

export const kUserStatusUnknown = 'unknown';
export const kUserStatusPendingTerms = 'tsandcs';
export const kUserStatusLoggedIn = 'logged in';
export const kUserStatusLoggedOut = 'logged out';

// This is a mirror of FeatureFlags in cem_server/user/models.py
export enum ProjectFeatures {
  TIMELINE = 'TL',
  VOLUMES = 'T',
  SPEEDS = 'RT',
  TRAVEL_TIME = 'TT',
  TIMELINE_SUMMARY = 'TLS',
  REALTIME = 'RL',
  TIMELINE_DAY_COMPARISON = 'TIMELINE_DAY_COMPARISON',
  INCIDENTS = 'INCIDENTS',
  ROUTE_MONITORING = 'RT',
  ALERTS = 'A',
}

export interface ProjectInfo {
  id?: number; // ProjectSetting id
  project_id?: number;
  project_name: string;
  project_slug?: string;
  project_center?: Point;
  project_boundary?: Polygon;
  is_default: boolean;
  available_bins: Array<number>;
  right_hand_drive: boolean;
  uses_metric: boolean;
  has_volume_model: boolean;
  features: Array<string>;
  uses_middle_east_day_types: boolean;
  disable_timeline_residential_roads: boolean;
  highway_types?: Array<string>;
  org_id: string;
  org_name: string;
  special_date_requests: SpecialDateRequest[];
  subscription_id: string;
  are_alerts_enabled?: boolean;
  is_sub_admin?: boolean;
}

export interface UserState {
  username: string;
  email: string;
  projects: Array<ProjectInfo>;
  // eslint-disable-next-line no-restricted-globals
  status: string; // TODO should be string enum
  initialSearchParams: string;
  errors: any;
  has_hidden_features: boolean;
  sso_username?: string;
  first_name?: string;
  last_name?: string;
  products: Array<ProductInfo>;
  has_sub_admin: boolean;
  insight_url: string;
}

const initialState: UserState = {
  username: '',
  email: '',
  projects: [],
  status: kUserStatusUnknown,
  errors: '',
  initialSearchParams: window.location.search,
  has_hidden_features: false,
  has_sub_admin: false,
  sso_username: undefined,
  first_name: '',
  last_name: '',
  products: [],
  insight_url: '',
};

export interface CurrentUserResponse {
  username: string;
  email: string;
  projects: ProjectInfo[];
  pending_terms: boolean;
  has_hidden_features: boolean;
  sso_username?: string;
}

export const loginSSO = createAsyncThunk(
  // action type string
  'users/ssoAuth',
  // payloadCreator callback function
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(
        `${restApiUrl}/users/sso-login/`,
        {
          // we validate 400 (Bad Request) so we get the error messages in response.data (not available in catch)
          validateStatus(status) {
            return [202, 400].includes(status);
          },
        },
      );
      // console.log(`axios post login response: ${JSON.stringify(response)}`);
      if (response.status === 202) {
        return response.data;
      }
      return thunkAPI.rejectWithValue({ error: response.data });
    } catch (err) {
      if (err instanceof Error) {
        console.error(`axios post login error: ${err.message}`);
        return thunkAPI.rejectWithValue({ error: err.message });
      } else {
        console.error('axios post login error:', err);
        return thunkAPI.rejectWithValue({
          error: `Unknown error occurred ${err}`,
        });
      }
    }
  },
);

export const loginUser = createAsyncThunk(
  // action type string
  'users/login',
  // payloadCreator callback function
  async (credentials: { username: string; password: string }, thunkAPI) => {
    try {
      const response = await axiosInstance.post(
        `${restApiUrl}/users/login/`,
        {
          username: credentials.username,
          password: credentials.password,
        },
        {
          // we validate 400 (Bad Request) so we get the error messages in response.data (not available in catch)
          validateStatus(status) {
            return [202, 400].includes(status);
          },
        },
      );
      // console.log(`axios post login response: ${JSON.stringify(response)}`);
      if (response.status === 202) {
        return response.data;
      }
      return thunkAPI.rejectWithValue({ error: response.data });
    } catch (err) {
      if (err instanceof Error) {
        console.error(`axios post login error: ${err.message}`);
        return thunkAPI.rejectWithValue({ error: err.message });
      } else {
        console.error('axios post login error:', err);
        return thunkAPI.rejectWithValue({
          error: `Unknown error occurred ${err}`,
        });
      }
    }
  },
);

export const signTerms = createAsyncThunk(
  // action type string
  'users/terms',
  // payloadCreator callback function
  async (
    form_info: {
      email: string;
      name: string;
      title: string;
      org: string;
      org_secondary?: string;
      confirmed: boolean;
      confirmed_date: string;
    },
    thunkAPI,
  ) => {
    try {
      console.log('Sending users/terms with payload', form_info);
      const response = await axiosInstance.post(
        `${restApiUrl}/users/terms/`,
        form_info,
        {
          // we validate 400 (Bad Request) so we get the error messages in response.data (not available in catch)
          validateStatus(status) {
            return [202, 400, 403].includes(status);
          },
        },
      );
      // console.log(`axios post login response: ${jstr(response)}`);
      if (response.status === 202) {
        return response.data;
      }
      return thunkAPI.rejectWithValue({ error: response.data });
    } catch (err) {
      if (err instanceof Error) {
        console.error(`axios post terms signature error: ${err.message}`);
        return thunkAPI.rejectWithValue({ error: err.message });
      } else {
        console.error('axios post terms signature error:', err);
        return thunkAPI.rejectWithValue({
          error: `Unknown error occurred ${err}`,
        });
      }
    }
  },
);

export const logoutUser = createAsyncThunk(
  // action type string
  'users/logout',
  // payloadCreator callback function
  async (credentials, thunkAPI) => {
    const response = await axiosInstance.post(`${restApiUrl}/users/logout/`);
    console.log(`axios post logout response: ${JSON.stringify(response.data)}`);
    return response.data;
  },
);

export const checkUserStatus = createAsyncThunk(
  // action type string
  'users/checkStatus',
  // payloadCreator callback function
  async (thunkAPI) => {
    const response = await axiosInstance.get(`${restApiUrl}/users/current/`);
    // console.log(`axios post checkUserStatus response: ${JSON.stringify(response.data)}`);
    walkmeService.addWalkmeVariables(response.data);
    return response.data;
  },
);

export const setUserStateFromSuccessfulUserDetail = (state, payload) => {
  if (!payload.pending_terms) {
    state.status = kUserStatusLoggedIn;
  } else {
    state.status = kUserStatusPendingTerms;
  }
  state.username = payload.username;
  state.email = payload.email;
  state.projects = payload.projects;
  state.has_hidden_features = payload.has_hidden_features;
  state.has_sub_admin = payload.has_sub_admin;
  state.sso_username = payload.sso_username;
  state.first_name = payload.first_name;
  state.last_name = payload.last_name;
  state.products = payload.products;
  state.insight_url = payload.insight_url;
};

function resetUserInner(state) {
  state = Object.assign(state, initialState);
  state.status = kUserStatusLoggedOut;
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetUser: (state) => {
      // https://redux.js.org/tutorials/essentials/part-5-async-logic
      // Immer lets us update state in two ways: either mutating the existing state value, or returning a new result.
      resetUserInner(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.fulfilled, (state, action) => {
        setUserStateFromSuccessfulUserDetail(state, action.payload);
        // console.log(`###### loginUser.fulfilled state: ${JSON.stringify(state)}`);
      })
      .addCase(loginUser.rejected, (state, action: any) => {
        state.status = kUserStatusLoggedOut;
        state.errors = action.payload.error;
        // console.log(`################## loginUser.rejected action: ${JSON.stringify(action)}`);
        // console.log(`################## loginUser.rejected state: ${JSON.stringify(state)}`);
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state = Object.assign(state, initialState); // returning a new state object
        state.status = kUserStatusLoggedOut;
        console.log(
          `logoutUser.fulfilled set state to: ${JSON.stringify(state)}`,
        );
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state = Object.assign(state, initialState); // returning a new state object
        state.status = kUserStatusLoggedOut; // ! is this right?
        state.errors = action.error.message;
        console.log(
          `logoutUser.rejected payload: ${JSON.stringify(action.error)}`,
        );
      })
      .addCase(checkUserStatus.fulfilled, (state, action) => {
        // console.log(`checkUserStatus.fulfilled action: ${JSON.stringify(action)}`);
        setUserStateFromSuccessfulUserDetail(state, action.payload);

        // console.log(`checkUserStatus.fulfilled set state to: ${JSON.stringify(state)}`);
      })
      .addCase(signTerms.fulfilled, (state, action) => {
        setUserStateFromSuccessfulUserDetail(state, action.payload);
      })
      .addCase(checkUserStatus.rejected, (state, action) => {
        // console.log(`checkUserStatus.rejected action: ${JSON.stringify(action)}`);
        state.status = kUserStatusLoggedOut;
        state.errors = action.error.message;
        // console.log(`checkUserStatus.rejected payload: ${JSON.stringify(action)}`);
      })
      .addCase('api/executeQuery/rejected', (state, action) => {
        // If we recieve a 403 error from django, this is an indicator we have been logged out, kick back to login
        if ((action as any).payload.status === 403) {
          state.status = kUserStatusUnknown;
        }
      });
  },
});

export function getProjectInfoForSlug(user, slug): ProjectInfo | undefined {
  for (let i = 0; i < user.projects.length; i++) {
    if (user.projects[i].project_slug === slug) {
      return user.projects[i];
    }
  }
  return undefined;
}

export function selectUserState(rootState) {
  return rootState.user;
}

export function selectUserStatus(rootState) {
  return selectUserState(rootState).status;
}

export function selectUserProducts(rootState): Array<ProductInfo> {
  return selectUserState(rootState).products;
}

const { actions, reducer } = userSlice;
export const { resetUser } = actions;
export default reducer;

export const selectUser = (state) => state.user;

/**
 * Log out a SSO user
 *
 * This function sends a POST request to the Relying Party (RP) SSO logout endpoint.
 * If the response status is 202, it retrieves the logout URL from the response data and
 * submits a logout request to the Identity Provider (IdP) by creating and submitting a form.
 *
 */
export const logoutSSOUser = createAsyncThunk(
  // action type string
  'users/sso-logout',
  // payloadCreator callback function
  async (credentials, thunkAPI) => {
    try {
      const response = await axiosInstance.post(
        `${restApiUrl}/users/sso-logout/`,
        undefined,
        {
          validateStatus(status) {
            return status === 202;
          },
        },
      );

      const { logout_url: logoutUrl } = response.data;

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = logoutUrl;
      document.body.appendChild(form);
      form.submit();

      return response.data;
    } catch (err) {
      console.error('axios post SSO logout error:', err);
      return thunkAPI.rejectWithValue({
        error: `Unknown error occurred ${err}`,
      });
    }
  },
);
